import i18n, { type Resource } from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'

export type LocalizationResources = Resource

export type LocalizationOptions = {
  resources: LocalizationResources
  defaultLanguage: string
  fallbackLanguage: string | string[]
  language?: string | null
  supportedLanguages?: readonly string[]
  aliases?: Record<string, string>
}

const applyAlias = (language: string, aliases?: Record<string, string>) => {
  if (!aliases) return language
  if (aliases[language]) return aliases[language]
  const lower = language.toLowerCase()
  for (const [key, value] of Object.entries(aliases)) {
    if (key.toLowerCase() === lower) {
      return value
    }
  }
  return language
}

const pickSupportedLanguage = (language: string, supported?: readonly string[]) => {
  if (!supported || supported.length === 0) return language
  const match = supported.find((item) => item.toLowerCase() === language.toLowerCase())
  return match ?? supported[0]
}

export const resolveLanguage = (
  language: string | null | undefined,
  options: { defaultLanguage: string; supportedLanguages?: readonly string[]; aliases?: Record<string, string> },
) => {
  const fallback = options.defaultLanguage
  if (!language || !language.trim()) {
    return fallback
  }
  const aliased = applyAlias(language.trim(), options.aliases)
  return pickSupportedLanguage(aliased, options.supportedLanguages)
}

export const initLocalization = (options: LocalizationOptions) => {
  const resolvedLanguage = resolveLanguage(options.language, {
    defaultLanguage: options.defaultLanguage,
    supportedLanguages: options.supportedLanguages,
    aliases: options.aliases,
  })

  i18n.use(initReactI18next).init({
    resources: options.resources,
    lng: resolvedLanguage,
    fallbackLng: options.fallbackLanguage,
    supportedLngs: options.supportedLanguages,
    interpolation: { escapeValue: false },
  })

  return i18n
}

export const t = i18n.t.bind(i18n)

export { useTranslation }
export { i18n as localization }
